// Machine provisions the three automation rules at connect, so the operator no
// longer builds them by hand — the Message and Status steps are gone. What is
// left is credentials, the identity Freshdesk returned, and a read-only summary
// of what KIM will do to their tickets.
export const FRESHDESK_STEPS = ['Credentials', 'Agent', 'Tags', 'Review'] as const;

/**
 * Address the operator gives the dedicated Freshdesk agent they create for KIM.
 * Mirrors the Kustomer convention (KUSTOMER_BOT_EMAIL in machine's helpers), so
 * the bot is recognisable by the same identity across helpdesks.
 *
 * Nothing validates it: unlike Kustomer, machine does not provision the Freshdesk
 * agent — the operator creates it and pastes ITS API key, and verification simply
 * reports whichever agent owns that key (agents/me). So this is a convention for
 * a consistent, recognisable reply identity, not a requirement.
 */
export const FRESHDESK_BOT_EMAIL = 'connect@kim.cc';

/**
 * Tags KIM applies to Freshdesk tickets. Mirrors kim-ai-backend's
 * RESPONDED_TICKET_TAG / SPAM_TICKET_TAG (env-overridable there). Shown for
 * reference only — nothing in the wizard creates or manages them, because
 * Freshdesk has no tag resource: tags are plain strings on a ticket.
 */
export const FRESHDESK_KIM_TAGS = [
  {
    name: 'responded-by-kim-ai',
    applied: 'When KIM sends an automated reply',
    why: 'Filter a view to everything KIM answered',
  },
  {
    name: 'closed-by-kim-ai',
    applied: 'When KIM marks a ticket as spam and closes it',
    why: 'Audit what KIM closed without a human',
  },
] as const;
// The custom-header contract (x-kim-account-id / x-kim-webhook-token) and the
// three automation-rule payloads used to be defined here for the operator to
// copy into Freshdesk by hand. Machine owns rule provisioning now — the
// authoritative shapes live in machine's backend/services/freshdesk.js, and
// keeping a second copy here only let the two drift apart silently.

const FRESHDESK_SUBDOMAIN_PATTERN = /^(?=.{1,63}$)[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

export const isValidFreshdeskSubdomain = (subdomain: string) =>
	FRESHDESK_SUBDOMAIN_PATTERN.test(subdomain) && subdomain !== 'localhost' && !subdomain.startsWith('xn--');

export interface FreshdeskRotationUiState<TRotation> {
	rotation: TRotation | null;
	/**
	 * The new token was displayed. Machine re-points the rules itself at
	 * activation, but the token is copyable — an operator MAY have pasted it
	 * into a rule by hand, so cancellation stays conservatively destructive
	 * once this is true.
	 */
	instructionsExposed: boolean;
}

export const resetFreshdeskRotationState = <TRotation>(): FreshdeskRotationUiState<TRotation> => ({
	rotation: null,
	instructionsExposed: false
});

export const stageFreshdeskRotationState = <TRotation>(rotation: TRotation): FreshdeskRotationUiState<TRotation> => ({
	rotation,
	instructionsExposed: true
});

// A staged rotation is all activation needs. The per-rule "I updated the
// physical rules" attestations are gone: machine re-points all three rules in
// one pass at activation, so the checkboxes demanded the operator attest to
// work they never did — and blocked "Confirm and rotate" until they did.
export const canActivateFreshdeskRotation = <TRotation>(state: FreshdeskRotationUiState<TRotation>) =>
	Boolean(state.rotation);

export const buildFreshdeskRotationCancellation = <TRotation>(state: FreshdeskRotationUiState<TRotation>) => ({
	remoteRulesEdited: state.instructionsExposed,
	confirmDestructive: state.instructionsExposed
});

export const isFreshdeskRotationExpired = (expiresAt: string, now = Date.now()) => {
	const expiry = new Date(expiresAt).getTime();
	return !Number.isFinite(expiry) || expiry <= now;
};

// Verification is now the only gate: the rule confirmations are gone, because
// machine builds the rules rather than asking the operator to attest to them.
export const canFinishFreshdeskSetup = (hasVerification: boolean) => hasVerification;

export const buildFreshdeskConnectPayload = (
	subdomain: string,
	apiKey: string,
	webhookToken: string,
	verificationReceipt: string
) => ({
	subdomain,
	api_key: apiKey,
	webhook_token: webhookToken,
	verification_receipt: verificationReceipt
});

export const getFreshdeskVerificationErrorCopy = (code: string) => {
	if (code === 'RATE_LIMITED') return { title: 'Freshdesk rate limit reached', body: 'Freshdesk is temporarily limiting verification requests. Wait about 60 seconds before trying again.' };
	if (['ACCESS_DENIED', 'PERMISSION_DENIED', 'REQUIRE_FEATURE'].includes(code)) return { title: 'Agent identity could not be verified', body: 'Freshdesk denied the agents/me profile check. Allow profile access or use another dedicated agent key, then retry.' };
	if (['PROVIDER_UNAVAILABLE', 'INVALID_AGENT_RESPONSE'].includes(code)) return { title: 'Freshdesk is unavailable', body: 'Freshdesk did not respond. Your credentials were not saved. Try again when the service is available.' };
	return { title: 'Invalid workspace or API key', body: 'Freshdesk could not verify this subdomain and API key. Check both values, then try again.' };
};

const FRESHDESK_API_KEY_REPLACEMENT_ERRORS: Record<string, string> = {
	AGENT_IDENTITY_MISMATCH: 'This key belongs to a different Freshdesk agent. The current reply identity and connection are unchanged.',
	AGENT_IDENTITY_UNVERIFIABLE: 'Kim could not safely verify the connected reply-agent identity. The current key and webhook token are unchanged.',
	CONNECTION_CHANGED: 'The Freshdesk connection changed during replacement. Refresh integrations before trying again.'
};

export const getFreshdeskApiKeyReplacementErrorCopy = (code: string, providerMessage = '') =>
	FRESHDESK_API_KEY_REPLACEMENT_ERRORS[code] ?? `${providerMessage || getFreshdeskVerificationErrorCopy(code).title}. The current key and webhook token are unchanged.`;

export type FreshdeskActivationReconciliation = 'resumable' | 'indeterminate';

export const reconcileFreshdeskActivation = (integration?: {
	isConnected?: boolean;
	authMetaData?: Record<string, unknown>;
}): FreshdeskActivationReconciliation => {
	if (!integration?.isConnected) return 'indeterminate';
	if (typeof integration.authMetaData?.webhook_token_pending_expires_at === 'string') return 'resumable';
	return 'indeterminate';
};

export const selectFreshdeskConnectedMetadata = (metadata: Record<string, unknown>) => ({
	subdomain: typeof metadata.subdomain === 'string' ? metadata.subdomain : '',
	agent: {
		id: typeof metadata.agent_id === 'string' ? metadata.agent_id : '',
		name: typeof metadata.agent_name === 'string' ? metadata.agent_name : null,
		email: typeof metadata.agent_email === 'string' ? metadata.agent_email : null
	},
	pendingExpiresAt: typeof metadata.webhook_token_pending_expires_at === 'string' ? metadata.webhook_token_pending_expires_at : null
});
